import _ from 'lodash'
import dayjs, { Dayjs } from 'dayjs'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Grid, Stack } from '@mui/material'

import JoinNetworkRequirementDialog from '@/components/ui/dialogs/JoinNetworkRequirementDialog'
import NetworkCard from '@/components/ui/cards/NetworkCard'
import DatePickerInput from '@/components/ui/inputs/DatePickerInput'
import TimePickerInput from '@/components/ui/inputs/TimePickerInput'

import { useGetNetworksQuery, useSendNetworkJoinRequestMutation } from '@/core/services/network'

import { useDateTimePickerOptions } from '@/hooks/useDateTimePickerOptions'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { getApiErrorMessage } from '@/utils/errors'
import { isConsultationOrder } from '@/utils/type-guards'

import { CreateConsultationStepperContext } from '../CreateConsultationStepper'

export default function SelectNetworkStep() {
  const { setValue, initialValues, values } = useContext(CreateConsultationStepperContext)
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)

  const { data: rawNetworks, refetch: refetchNetworks } = useGetNetworksQuery({}, { refetchOnMountOrArgChange: true })
  const [joinNetwork, { isLoading: isRequestingJoin }] = useSendNetworkJoinRequestMutation()

  const { currentDateTime, datePickerValue, setDatePickerValue, timePickerOptions, timePickerValue, setTimePickerValue } = useDateTimePickerOptions(
    initialValues.startDate && dayjs(initialValues.startDate).isValid() ? dayjs(initialValues.startDate).toDate() : undefined
  )

  const [requiredJoinNetworkId, setRequiredJoinNetworkId] = useState<string>()

  const networks = useMemo(
    () =>
      _.map(rawNetworks, (network) => ({
        id: network._id,
        coverPhoto: network.coverPhoto?.url,
        joined: !!network.members.find((member) => member === authUser?._id),
        memberCount: network.members.length,
        name: network.name,
        pendingOrders: [
          {
            count: _.filter(network.pendingOrders, isConsultationOrder).length,
            type: 'consultation' as const,
          },
        ],
        privacy: network.privacy,
      })),
    [rawNetworks, authUser]
  )

  useEffect(() => {
    if (values.network) setValue('network', undefined)
    // eslint-disable-next-line
  }, [])

  const handleChangeDatePickerValue = (date: Dayjs | null) => {
    setDatePickerValue(date)
    const startDate =
      date && timePickerValue && dayjs(date).isValid() && dayjs(timePickerValue).isValid()
        ? dayjs(date).set('hour', timePickerValue.get('hour')).set('minute', timePickerValue.get('minute')).toDate()
        : null
    setValue('startDate', startDate ? startDate.toISOString() : undefined)
  }

  const handleChangeTimePickerValue = (time: Dayjs | null) => {
    setTimePickerValue(time)
    const startDate =
      datePickerValue && time && dayjs(datePickerValue).isValid() && dayjs(time).isValid()
        ? dayjs(datePickerValue).set('hour', time.get('hour')).set('minute', time.get('minute')).toDate()
        : null
    setValue('startDate', startDate ? startDate.toISOString() : undefined)
  }

  const error = values.startDate && dayjs(values.startDate).isValid() ? dayjs(values.startDate).isBefore(currentDateTime, 'minutes') : false

  return (
    <Stack spacing={3}>
      <div>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <DatePickerInput
              disablePast
              error={error}
              helperText={error && t('yup:date.minDate', { min: currentDateTime.format('DD/MM/YYYY HH[h]mm') })}
              value={datePickerValue}
              onChange={handleChangeDatePickerValue}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TimePickerInput
              error={error}
              value={timePickerValue}
              onChange={handleChangeTimePickerValue}
              options={timePickerOptions}
            />
          </Grid>
        </Grid>
      </div>
      <div>
        <Grid container spacing={2}>
          {networks.map((network) => {
            const isJoinRequestPending =
              network.privacy === 'private' &&
              !!_.find(rawNetworks, ['_id', network.id])?.pendingMembers.find((pendingMember) => pendingMember._id === authUser?._id)
            return (
              <Grid key={network.id} item xs={12} md={6}>
                <NetworkCard
                  isJoinRequestPending={isJoinRequestPending}
                  network={network}
                  onSelect={(networkId) => {
                    const network = rawNetworks?.find((network) => network._id === networkId)

                    if (!network) return

                    if (network.privacy === 'public' || !!_.find(network?.members, (member) => member === authUser?._id)) {
                      setValue('network', values.network !== networkId ? networkId : undefined)
                    } else {
                      setRequiredJoinNetworkId(network._id)
                    }
                  }}
                  selected={network.id === values.network}
                />
              </Grid>
            )
          })}
        </Grid>
      </div>
      <JoinNetworkRequirementDialog
        isRequesting={isRequestingJoin}
        open={!!requiredJoinNetworkId}
        onClose={() => {
          setRequiredJoinNetworkId(undefined)
        }}
        onJoin={async () => {
          if (!requiredJoinNetworkId) return
          try {
            await joinNetwork(requiredJoinNetworkId).unwrap()
            refetchNetworks()
            setRequiredJoinNetworkId(undefined)
          } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, t('notification.network.requestJoin.error')), { variant: 'error' })
          }
        }}
      />
    </Stack>
  )
}
